import React, { useEffect, useRef, useState } from 'react';

/**
 * Hero3D — the Modernist hero centrepiece.
 * A red wireframe cage around a faceted ink core, on a receding grid plate.
 * Reacts to cursor and scroll; recolours itself when the variant changes.
 *
 * Only ever mounted inside the Modernist shell, which App renders only under a
 * modernist-* theme — so `import('three')` (~173 kB) never loads on the other
 * themes, and this file stays out of the main bundle.
 */
const prefersStill = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Hero3D = ({ className = 'm-hero__3d' }) => {
  const hostRef = useRef(null);

  /**
   * A permanently spinning WebGL scene is exactly what reduced motion asks us
   * not to render, and there is no still frame worth the download — so on that
   * preference the canvas is skipped outright and three.js is never fetched.
   * Tracked rather than read once, since the setting can change mid-session.
   */
  const [active, setActive] = useState(() => !prefersStill());

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setActive(!query.matches);
    // Safari < 14 only has the deprecated listener API.
    if (query.addEventListener) query.addEventListener('change', sync);
    else query.addListener(sync);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', sync);
      else query.removeListener(sync);
    };
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    const host = hostRef.current;
    if (!host) return;
    let alive = true;
    let cleanup = () => {};

    const readTheme = () => {
      const cs = getComputedStyle(document.documentElement);
      return {
        accent: (cs.getPropertyValue('--m-3d-accent') || '#ec3013').trim(),
        ink: (cs.getPropertyValue('--m-3d-ink') || '#201e1d').trim()
      };
    };

    import('three').then((THREE) => {
      if (!alive) return;
      const { accent, ink } = readTheme();

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.domElement.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block';
      host.appendChild(renderer.domElement);

      const root = new THREE.Group();
      scene.add(root);

      const cageMat = new THREE.LineBasicMaterial({ color: accent });
      const cage = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.IcosahedronGeometry(2.35, 1)), cageMat
      );
      root.add(cage);

      const coreMat = new THREE.MeshStandardMaterial({ color: ink, flatShading: true, roughness: 0.55, metalness: 0.1 });
      const core = new THREE.Mesh(new THREE.IcosahedronGeometry(1.35, 0), coreMat);
      root.add(core);

      const grid = new THREE.GridHelper(9, 9, ink, ink);
      grid.material.transparent = true;
      grid.material.opacity = 0.18;
      grid.position.y = -3.1;
      root.add(grid);

      scene.add(new THREE.AmbientLight(0xffffff, 0.75));
      const key = new THREE.DirectionalLight(0xffffff, 1.5);
      key.position.set(4, 6, 5);
      scene.add(key);
      const fill = new THREE.DirectionalLight(accent, 0.9);
      fill.position.set(-5, -2, 3);
      scene.add(fill);

      let fitZ = 10.5;
      const fit = () => {
        const w = host.clientWidth, h = host.clientHeight;
        if (!w || !h) return;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
        const vFit = 3.4 / Math.tan((camera.fov * Math.PI / 180) / 2);
        fitZ = Math.max(8.5, Math.min(13, vFit / Math.min(1, Math.max(0.72, camera.aspect))));
      };
      fit();
      const ro = new ResizeObserver(fit);
      ro.observe(host);

      let mx = 0, my = 0, tx = 0, ty = 0, scrollN = 0;
      const onMove = (e) => {
        const r = host.getBoundingClientRect();
        tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
        ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
      };
      const onScroll = () => { scrollN = Math.min(1, window.scrollY / (window.innerHeight * 1.4)); };
      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('scroll', onScroll, { passive: true });

      // recolour on theme change
      const themeObserver = new MutationObserver(() => {
        const t = readTheme();
        cageMat.color.set(t.accent);
        fill.color.set(t.accent);
        coreMat.color.set(t.ink);
        grid.material.color.set(t.ink);
      });
      themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

      const clock = new THREE.Clock();
      let raf;
      const tick = () => {
        if (!alive) return;
        raf = requestAnimationFrame(tick);
        const t = clock.getElapsedTime();
        mx += (tx - mx) * 0.06;
        my += (ty - my) * 0.06;
        cage.rotation.y = t * 0.22;
        cage.rotation.x = t * 0.12;
        core.rotation.y = -t * 0.35;
        core.rotation.z = t * 0.18;
        root.rotation.y = mx * 0.4 + scrollN * 1.1;
        root.rotation.x = my * 0.28 - scrollN * 0.35;
        root.position.y = -scrollN * 1.2;
        camera.position.z = fitZ + scrollN * 2.6;
        renderer.render(scene, camera);
      };
      tick();

      cleanup = () => {
        cancelAnimationFrame(raf);
        ro.disconnect();
        themeObserver.disconnect();
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('scroll', onScroll);
        renderer.dispose();
        if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      };
    }).catch(() => {});

    return () => { alive = false; cleanup(); };
  }, [active]);

  // No node at all when inactive: keeps the DOM clean and guarantees the effect
  // above cannot find a host to mount into.
  if (!active) return null;

  return <div ref={hostRef} className={className} aria-hidden="true" />;
};

export default Hero3D;
